import { z } from "zod";
import { EXPENSE_STATUS } from "./expenses.js";

/* ===========================================================================
 * US-E7.F2-0049 — Open Expense Decision (@UC0035/@UC0052)
 * POST /v1/expenses/:id/approve | reject | open-vote — Phase 8 Consensus + Phase 6 LEDGER.
 *
 * approve (config admin_only) → post EXPENSE (DEBIT EXPENSE / CREDIT CASH) qua LedgerService.
 * reject → status rejected (KHÔNG ledger). open-vote → tạo Consensus session.
 * Tiền STRING minor unit (B1, no float).
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

export const EXPENSE_VOTE_CONFIG_MISSING_MESSAGE =
  "Vui lòng cấu hình tỉ lệ và thời hạn vote trước.";

/** Consensus mechanism (snapshot lúc mở). `silent` = im-lặng-duyệt (US-E17.F3-0190 @UC9258). */
export const CONSENSUS_MECHANISM = {
  MAJORITY: "majority",
  RATIO: "ratio",
  ADMIN_ONLY: "admin_only",
  SILENT: "silent",
} as const;

export type ConsensusMechanism =
  (typeof CONSENSUS_MECHANISM)[keyof typeof CONSENSUS_MECHANISM];

/** Whitelist mechanism (CHECK db `consensus_mechanism_check` MIRROR đúng danh sách này). */
export const CONSENSUS_MECHANISMS = [
  CONSENSUS_MECHANISM.MAJORITY,
  CONSENSUS_MECHANISM.RATIO,
  CONSENSUS_MECHANISM.ADMIN_ONLY,
  CONSENSUS_MECHANISM.SILENT,
] as const;

/* ---- approve → post EXPENSE ---- */
export const approveExpenseResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    // US-E14.F5-0174-S03: nullable — source=peer (chia 100%) KHÔNG post EXPENSE ledger (0 fund-flow).
    ledgerTxId: z.string().uuid().nullable(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    newStatus: z.literal(EXPENSE_STATUS.POSTED),
    approvedAt: z.string().datetime(),
    // US-E14.F5-0174-S03: opt-in chi hộ — proposal ids sinh (chưa nợ tới khi accept). Vắng → [].
    payOnBehalfProposals: z.array(z.string().uuid()).default([]),
  })
  .strict();

export type ApproveExpenseResponse = z.infer<
  typeof approveExpenseResponseSchema
>;

/* ---- reject → no ledger ---- */
export const rejectExpenseRequestSchema = z
  .object({
    reason: z.string().trim().max(500).optional(),
  })
  .strict();

export type RejectExpenseRequest = z.infer<typeof rejectExpenseRequestSchema>;

export const rejectExpenseResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    newStatus: z.literal(EXPENSE_STATUS.REJECTED),
    rejectedAt: z.string().datetime(),
  })
  .strict();

export type RejectExpenseResponse = z.infer<
  typeof rejectExpenseResponseSchema
>;

/* ---- open-vote → Consensus session ---- */
export const openVoteResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    consensusId: z.string().uuid(),
    // US-E17.F3-0190 (@UC9258): `silent` = phiên im-lặng-duyệt (khoản GIỮA 2 ngưỡng, auto-mode).
    mechanism: z.enum([
      CONSENSUS_MECHANISM.MAJORITY,
      CONSENSUS_MECHANISM.RATIO,
      CONSENSUS_MECHANISM.SILENT,
    ]),
    thresholdRatio: z.number().nullable(),
    deadline: z.string().datetime().nullable(),
    status: z.literal("open"),
    openedAt: z.string().datetime(),
  })
  .strict();

export type OpenVoteResponse = z.infer<typeof openVoteResponseSchema>;

/* ===========================================================================
 * US-E17.F3-0190 — reopen (@UC9259) + dissent muộn (@UC9260) + tie-break (@UC9261)
 * ========================================================================= */

export const REOPEN_REASON_MAX = 500;
export const DISSENT_REASON_MAX = 500;

/** Chỉ phiên ĐÃ ĐÓNG-KHÔNG-THÔNG-QUA (failed/expired) mới mở lại được. */
export const REOPENABLE_CONSENSUS_STATUSES = ["failed", "expired"] as const;

export const REOPEN_NOT_CLOSED_MESSAGE =
  "Chỉ mở lại được phiên đã đóng và không thông qua.";
export const DISSENT_NOT_PASSED_MESSAGE =
  "Chỉ ghi phản đối muộn cho phiên đã thông qua.";

/** POST /v1/expenses/:id/consensus/reopen — lý do BẮT BUỘC (dấu vết vì sao mở lại). */
export const reopenConsensusRequestSchema = z
  .object({
    reason: z.string().trim().min(1).max(REOPEN_REASON_MAX),
  })
  .strict();

export type ReopenConsensusRequest = z.infer<typeof reopenConsensusRequestSchema>;

export const reopenConsensusResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    /** Phiên MỚI (append-only — phiên cũ GIỮ NGUYÊN status/lịch sử/phiếu). */
    consensusId: z.string().uuid(),
    reopenedFrom: z.string().uuid(),
    mechanism: z.enum(CONSENSUS_MECHANISMS),
    status: z.literal("open"),
    deadline: z.string().datetime().nullable(),
    reopenedAt: z.string().datetime(),
  })
  .strict();

export type ReopenConsensusResponse = z.infer<typeof reopenConsensusResponseSchema>;

/** POST /v1/expenses/:id/consensus/dissent — phản đối MUỘN (sau passed). KHÔNG lật kết quả. */
export const recordDissentRequestSchema = z
  .object({
    reason: z.string().trim().min(1).max(DISSENT_REASON_MAX),
  })
  .strict();

export type RecordDissentRequest = z.infer<typeof recordDissentRequestSchema>;

export const recordDissentResponseSchema = z
  .object({
    dissentId: z.string().uuid(),
    consensusId: z.string().uuid(),
    expenseId: z.string().uuid(),
    /** 🔴 BẤT BIẾN: dissent KHÔNG lật kết quả — luôn là status của phiên (passed). */
    consensusStatus: z.literal("passed"),
    recordedAt: z.string().datetime(),
  })
  .strict();

export type RecordDissentResponse = z.infer<typeof recordDissentResponseSchema>;

/* ---- tie-break + silent config (@UC9258 @UC9261) ---- */
/** Khoá registry `system_settings` (override được ở Group/Fund settings) — MIRROR domain. */
export const SILENT_APPROVE_HOURS_KEY = "silent_approve_hours";
export const TIE_BREAK_KEY = "tie_break";

export const CONSENSUS_F3_CONFIG_KEY_LIST = [
  SILENT_APPROVE_HOURS_KEY,
  TIE_BREAK_KEY,
] as const;

export const TIE_BREAK_POLICY = {
  REJECT: "reject",
  OWNER_DECIDES: "owner_decides",
} as const;

export const tieBreakSchema = z.enum([
  TIE_BREAK_POLICY.REJECT,
  TIE_BREAK_POLICY.OWNER_DECIDES,
]);

export type TieBreakPolicyContract = z.infer<typeof tieBreakSchema>;

/** Config: số giờ im lặng ⇒ auto pass (1..720). */
export const silentApproveHoursSchema = z.number().int().min(1).max(720);

/* ---- US-E17.F5-0192 (@UC9265) — 🔴 hạn MỞ TỐI ĐA của phiên (chống phiên treo vĩnh viễn) ---- */
/**
 * Khoá registry `system_settings` — MIRROR domain `stuck-consensus.ts`.
 * `openVote` mode `majority` (và mọi mode không tự có deadline) LẤY key này để tính `deadline`
 * ⇒ KHÔNG còn phiên `deadline=null` treo vĩnh viễn. Job dọn cũng dùng key này làm TTL cho phiên
 * LEGACY (đẻ trước fix, deadline=null).
 */
export const VOTE_MAX_OPEN_HOURS_KEY = "vote_max_open_hours";
/** Mặc định 168h = 7 ngày. */
export const VOTE_MAX_OPEN_HOURS_DEFAULT = 168;
export const voteMaxOpenHoursSchema = z.number().int().min(1).max(720);
